import React from 'react';

import {assert} from 'Asserts';
import FileStates from 'FileStates';
import staticURL from 'lib/staticURL';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import AlphaFoldTabState from 'molbio/aa-sequences/alphafold/AlphaFoldTabState';
import LoggedOutModal, {LOGGED_OUT_MODAL_KEY} from 'nav/LoggedOutModal';
import EntityStore from 'stores/EntityStore';
import {getIsPhoneWidth} from 'ui-library/hooks/useIsPhoneWidth';
import * as ModalContainer from 'ui-library/ModalContainer';
import * as ImportUtil from 'util/ImportUtil';
import {withLoadingScreen} from 'util/Promises';
import {assertNever} from 'util/Types';

import * as ChatDAO from '../ChatDAO';
import {ChatInputGlobalClientState} from '../ChatInput';
import {setInitialChatData} from '../InitialChatData';
import {mapAgentModeToAgentType} from '../sidepanel/ChatTypeUtil';
import {ChatTypeByAgentMode} from '../sidepanel/ChatTypeUtil';
import {PromptSuggestion} from './PromptSuggestionTypes';

async function openAlphaFoldTab(proteinId: string): Promise<void> {
  const fileState = await FileStates.get().openFolderItem(proteinId);
  assert(fileState != null, 'Expected fileState to be defined', {fileId: proteinId});
  const alphaFoldTab = fileState.findTab(AlphaFoldTabState);
  if (!alphaFoldTab) {
    throw new Error(`Expected protein file to have an AlphaFoldTabState`);
  }
  // On mobile, we don't have the screen real estate to show a split workspace.
  if (getIsPhoneWidth()) {
    fileState.setIsWorkspaceSplit(false);
  }
  alphaFoldTab.set({active: true});
}

export default async function runPromptSuggestion(
  suggestion: PromptSuggestion,
  onProgress: (message: string) => void
): Promise<string | null> {
  switch (suggestion.action.type) {
    case 'NEW_CHAT': {
      const fileUploads = [];
      const contextIds = [];
      switch (suggestion.action.input.type) {
        case 'demo-file': {
          onProgress('Finding file...');
          const path = `/static/llm/example-data/${suggestion.action.input.filename}`;
          const blob = await (await fetch(staticURL(path))).blob();
          onProgress('Uploading file...');
          const s3Location = (await ImportUtil.upload(new File([blob], suggestion.action.input.filename)))!;
          fileUploads.push({s3Location, filename: suggestion.action.input.filename, mediaType: blob.type});
          break;
        }
        case 'benchling-objects': {
          contextIds.push(...suggestion.action.input.ids);
          break;
        }
        case 'none': {
          break;
        }
        default: {
          throw new Error(
            `Failed to run prompt suggestion. Unexpected input type: ${assertNever(suggestion.action.input).type}`
          );
        }
      }
      const message = {
        text: suggestion.action.prompt,
        fileUploads,
      };
      onProgress('Creating chat...');
      const chatType = ChatTypeByAgentMode[suggestion.action.agentMode];
      const chatId = await ChatDAO.createChatSession(chatType);
      const messagePromise = ChatDAO.appendDisplayMessageToChatSession({
        chatSessionId: chatId,
        agentType: mapAgentModeToAgentType(suggestion.action.agentMode),
        messageBlocks: ChatDAO.getMessageBlockInput(message),
        userChatContext: ChatDAO.getUserChatContext({contextIds}),
      });
      setInitialChatData(chatId, {
        message: {
          ...message,
          agentType: mapAgentModeToAgentType(suggestion.action.agentMode),
        },
        messagePromise,
      });
      ChatInputGlobalClientState.updateState('', {
        lastUsedAgent: suggestion.action.agentMode,
        lastUsedPublicDataEnabled: false,
      });
      openChat(chatId);
      return chatId;
    }
    case 'NEW_CHAT_PLACEHOLDER': {
      assert(
        EntityStore.currentUser() == null,
        'NEW_CHAT_PLACEHOLDER should only be shown to logged out users',
        {suggestion}
      );
      ModalContainer.open(
        LOGGED_OUT_MODAL_KEY,
        <LoggedOutModal
          onRequestClose={() => {
            ModalContainer.close(LOGGED_OUT_MODAL_KEY);
          }}
        />
      );
      return null;
    }
    case 'MODEL_HUB_OPEN_3D_STRUCTURE': {
      const {input} = suggestion.action;
      assert(input.type === 'benchling-objects', 'Expected benchling-objects input', {
        suggestion,
      });
      onProgress('Opening protein file...');
      // openFolderItem will unexpand the sidepanel, and on mobile that triggers AISidepanel to close itself,
      // so the onProgress above is not visible. To compensate, use a loading screen.
      const maybeLoadingScreen = getIsPhoneWidth() ? withLoadingScreen : <T,>(p: T) => p;
      await maybeLoadingScreen(openAlphaFoldTab(input.ids[0]), 'Opening protein file...');
      return null;
    }
    default:
      throw new Error(`Unknown action type: ${assertNever(suggestion.action).type}`);
  }
}
